import { animate, style, transition, trigger } from '@angular/animations';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  ViewChild,
  ChangeDetectionStrategy,
  input,
  effect
} from '@angular/core';
import { EMPTY, from, Subject, Subscription, timer } from 'rxjs';
import { catchError, take, takeUntil } from 'rxjs/operators';
import { ChatConversationService } from '../../../services/chat-conversation.service';
import { ChatLoggingService } from '../../../services/chat-logging.service';

@Component({
  selector: 'app-video-player',
  templateUrl: 'video-player.component.html',
  styleUrls: ['video-player.component.scss'],
  animations: [
    trigger('fadeInOut', [
      transition(':enter', [style({ opacity: 0 }), animate(200, style({ opacity: 1 }))]),
      transition(':leave', [animate(200, style({ opacity: 0 }))])
    ])
  ],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VideoPlayerComponent {
  // Signal-based inputs
  conversationId = input.required<string>();
  attachmentId = input.required<string>();
  currentTabIndex = input<string>();

  @ViewChild('videoElement', { static: true }) videoElement: ElementRef<HTMLVideoElement>;
  @ViewChild('srcEl', { static: true }) srcEl: ElementRef<any>;

  showControls = false;
  showInitial = true;
  showDuration = false;
  currentTime = '00:00';
  duration = '00:00';
  currentPercent = 0;
  isTypeSupported = true;

  // Getters for video state that work with DOM properties
  get isPaused(): boolean {
    return this.videoElement?.nativeElement?.paused ?? true;
  }

  get isMuted(): boolean {
    return this.videoElement?.nativeElement?.volume === 0;
  }

  hasMediaError = false;

  private _elapsedTimer = timer(1000, 1000);
  private _elapsedSubscription: Subscription;
  private _mediaSource: MediaSource;
  private _sourceBuffer: SourceBuffer;
  private _blobUrl: string;
  private _onSourceOpen: (() => Promise<void>) | null = null;
  private _onUpdateEnd: (() => void) | null = null;
  private ngUnsubscribe = new Subject<void>();

  constructor(
    private cdRef: ChangeDetectorRef,
    private chatConversationService: ChatConversationService,
    private chatLoggingService: ChatLoggingService
  ) {
    // Effect: Update ARIA attributes when video metadata loads for accessibility
    effect(() => {
      const conversationId = this.conversationId();
      const attachmentId = this.attachmentId();

      if (conversationId && attachmentId && this.videoElement?.nativeElement) {
        // Set accessibility attributes for screen readers
        const videoEl = this.videoElement.nativeElement;
        videoEl.setAttribute('aria-label', `Video attachment ${attachmentId}`);

        if (this.showDuration && this.duration !== '00:00') {
          videoEl.setAttribute('aria-description', `Video duration: ${this.duration}`);
        }
      }
    });
  }

  ngOnInit(): void {
    this.registerEventHandlers();
    this.downloadAttachment();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    if (this._blobUrl) {
      window.URL.revokeObjectURL(this._blobUrl);
    }
    if (this._sourceBuffer && this._onUpdateEnd) {
      this._sourceBuffer.removeEventListener('updateend', this._onUpdateEnd);
      this._onUpdateEnd = null;
    }
    if (this._mediaSource) {
      if (this._onSourceOpen) {
        this._mediaSource.removeEventListener('sourceopen', this._onSourceOpen);
        this._onSourceOpen = null;
      }
      if (this._mediaSource.readyState === 'open') {
        this._mediaSource.endOfStream();
      }
    }
  }

  downloadAttachment(): void {
    const conversationId = this.conversationId();
    const attachmentId = this.attachmentId();

    if (conversationId && attachmentId) {
      from(
        this.chatConversationService.getAttachmentAsBlob({
          conversationId,
          attachmentId
        })
      )
        .pipe(
          take(1),
          catchError((error: any) => {
            this.chatLoggingService.error({
              logMessage: '[video-player.downloadAttachment()]',
              logParameters: [this.conversationId(), this.attachmentId(), error]
            });
            return EMPTY;
          }),
          takeUntil(this.ngUnsubscribe)
        )
        .subscribe((blob: Blob) => this.handleVideoBlobResponse(blob));
    }
  }

  togglePlay(): void {
    if (this.isPaused) {
      this.videoElement.nativeElement.play();
      this.showInitial = false;
      this.showControls = true;
    } else {
      this.videoElement.nativeElement.pause();
      this.showInitial = true;
      this.showControls = false;
    }
    this.cdRef.detectChanges();
  }

  toggleVolume(): void {
    if (this.isMuted) {
      this.videoElement.nativeElement.volume = 1;
    } else {
      this.videoElement.nativeElement.volume = 0;
    }
  }

  async toggleFullScreen(): Promise<void> {
    try {
      await this.videoElement.nativeElement.requestFullscreen();
    } catch (e) {
      this.chatLoggingService.error({
        logMessage: '[video-player.toggleFullScreen()]',
        logParameters: [this.conversationId(), this.attachmentId(), e]
      });
    }
  }

  private registerEventHandlers(): void {
    this.videoElement.nativeElement.onplaying = _ => this.handlePlaying();
    this.videoElement.nativeElement.onvolumechange = _ => this.cdRef.detectChanges();
    this.videoElement.nativeElement.ontimeupdate = _ => this.handleTimeUpdate();
    this.videoElement.nativeElement.onpause = _ => this.handlePause();
    this.videoElement.nativeElement.onloadedmetadata = _ => this.handleLoadedMetadata();
  }

  private getFormattedTime(seconds: number): string {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);

    const formattedMinutes = m >= 10 ? m : '0' + m;
    const formattedSeconds = s >= 10 ? s : '0' + s;

    return `${formattedMinutes}:${formattedSeconds}`;
  }

  private handleVideoBlobResponse(blob: Blob): void {
    this.chatLoggingService.info({
      logMessage: '[video-player.handleVideoBlobResponse()]',
      logParameters: [
        this.conversationId(),
        this.attachmentId(),
        {
          codec: blob.type,
          isTypeSupported: MediaSource.isTypeSupported(blob.type)
        }
      ]
    });
    // MSE path: only reached when the server provides a MIME type that includes a full codec
    // string, e.g. 'video/mp4; codecs="avc1.42E01E,mp4a.40.2"'. Bare types such as
    // 'video/mp4' or 'video/quicktime' always return false here, so in practice playback
    // falls through to the <source> element fallback below.
    if (MediaSource.isTypeSupported(blob.type)) {
      this._mediaSource = new MediaSource();
      this._blobUrl = URL.createObjectURL(this._mediaSource);
      this.videoElement.nativeElement.src = this._blobUrl;
      // Stored as class properties so ngOnDestroy can removeEventListener and avoid leaking
      // closures on the MediaSource / SourceBuffer across component create/destroy cycles.
      this._onSourceOpen = async () => {
        this._sourceBuffer = this._mediaSource.addSourceBuffer(blob.type);

        this._onUpdateEnd = () => {
          if (this._mediaSource.readyState === 'open') {
            this._mediaSource.endOfStream();
            this.duration = this.getFormattedTime(this.videoElement.nativeElement.duration);
            this.showDuration = true;
            this.cdRef.detectChanges();
          }
        };
        this._sourceBuffer.addEventListener('updateend', this._onUpdateEnd);

        this._sourceBuffer.onerror = event => {
          this.chatLoggingService.error({
            logMessage: '[video-player.sourceBuffer.error]',
            logParameters: [this.conversationId(), this.attachmentId(), event]
          });
          this.hasMediaError = true;
          this.cdRef.detectChanges();
        };

        if (this._mediaSource.readyState === 'open' && !!this._sourceBuffer && !this._sourceBuffer.updating) {
          this._sourceBuffer.appendBuffer(await this.convertBlobToArrayBuffer(blob));
        }
      };
      this._mediaSource.addEventListener('sourceopen', this._onSourceOpen);
    } else {
      // Fallback path taken for all current server-provided video types.
      this._blobUrl = URL.createObjectURL(blob);
      this.srcEl.nativeElement.src = this._blobUrl;
      this.videoElement.nativeElement.load();
    }
  }

  private resetPlayer(): void {
    this.videoElement.nativeElement.currentTime = 0;
    this.showControls = false;
    this.showInitial = true;
    this.cdRef.detectChanges();
  }

  private handleTimeUpdate(): void {
    this.currentPercent =
      (this.videoElement.nativeElement.currentTime / this.videoElement.nativeElement.duration) * 100;
  }

  private handlePause(): void {
    this.chatLoggingService.info({
      logMessage: '[video-player.handlePause()]',
      logParameters: [this.conversationId(), this.attachmentId()]
    });
    // Assume that if we get pause and we're at the full duration of the video - we're done
    // Reset the player to the begining.
    if (this.videoElement.nativeElement.currentTime === this.videoElement.nativeElement.duration) {
      this.resetPlayer();
    }

    if (!!this._elapsedSubscription) {
      this._elapsedSubscription.unsubscribe();
    }
  }

  private handlePlaying(): void {
    this.chatLoggingService.info({
      logMessage: '[video-player.handlePlaying()]',
      logParameters: [this.conversationId(), this.attachmentId()]
    });

    this._elapsedSubscription = this._elapsedTimer.pipe(takeUntil(this.ngUnsubscribe)).subscribe(_ => {
      this.currentTime = this.getFormattedTime(this.videoElement.nativeElement.currentTime);
      this.cdRef.detectChanges();
    });
  }

  private handleLoadedMetadata(): void {
    this.chatLoggingService.info({
      logMessage: '[video-player.handleLoadedMetadata()]',
      logParameters: [this.conversationId(), this.attachmentId(), this.videoElement.nativeElement.duration]
    });

    if (this.videoElement.nativeElement.duration !== Infinity) {
      this.duration = this.getFormattedTime(this.videoElement.nativeElement.duration);
      this.showDuration = true;

      if (!this.cdRef['destroyed']) {
        this.cdRef.detectChanges();
      }
    }
  }

  private async convertBlobToArrayBuffer(blob: Blob): Promise<ArrayBuffer> {
    return new Response(blob).arrayBuffer();
  }
}
